import LocalizationService from "services/localizationService";
import { computed } from "mobx";
import { bound } from "@frui.ts/helpers";
import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { interfaces } from "inversify";
import OutageService from "services/outageService";
import { startOfCzechDay, utcToCZZonedTime } from "utils/dateUtils";
import ChargeOrder from "models/chargeOrder";
import ChargeOrderService from "services/chargeOrderService";
import { isAfter } from "date-fns";

export default class SelectPaymentMethodViewModel extends ScreenBase {
  private dateRegexp = /%date%/g;

  constructor(
    public chargeOrder: ChargeOrder,
    private localizationService: LocalizationService,
    private outageService: OutageService,
    private chargeOrderService: ChargeOrderService
  ) {
    super();
  }

  @bound
  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("payment", code);
  }

  @computed
  get transferDisabled() {
    if (this.earliestPossibleDate) {
      return this.chargeOrderStatus.charges.reduce(
        (summary, item) =>
          summary ||
          (item.validity.validSinceDate !== undefined &&
            isAfter(
              startOfCzechDay(new Date(this.earliestPossibleDate)),
              startOfCzechDay(new Date(item.validity.validSinceDate))
            )),
        false
      );
    }
    return false;
  }

  @computed
  get earliestPossibleDate() {
    return (
      this.chargeOrderService.chargeOrder._earliestPossibleValidSinceDate ??
      this.chargeOrderService.earliestPossibleValidSinceDate
    );
  }

  @computed
  get chargeOrderStatus() {
    return this.chargeOrderService.chargeOrder ?? this.chargeOrder;
  }

  @computed
  get payGateDisabled() {
    const outage = this.outageService.payGateActiveOutage;

    return outage !== undefined;
  }

  @computed
  get paymentTypeLabel() {
    if (this.transferDisabled && this.earliestPossibleDate) {
      const czechEarliestBankTransferDate = utcToCZZonedTime(new Date(this.earliestPossibleDate));
      const czechDisplayDate = this.localizationService.formatDate(czechEarliestBankTransferDate);

      return this.translateAttribute("transferDisabledInfo").replace(this.dateRegexp, czechDisplayDate);
    }

    return this.translateAttribute("transfer");
  }

  @computed
  get enableGooglePay() {
    // Google Pay is disabled for now
    // https://gitlab.cendis.cz/edaz/backend/all-in-one-be/-/issues/1217
    // return false;

    // TODO: Remove this method once Google Pay is accepted to production.
    return true;
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) => {
      return new SelectPaymentMethodViewModel(
        chargeOrder,
        container.get(LocalizationService),
        container.get(OutageService),
        container.get(ChargeOrderService)
      );
    };
  }
}
